import React, { useEffect, useState } from "react";
import { Loader2, Save, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const SOCIALS = [
  ["facebook", "Facebook"], ["instagram", "Instagram"], ["twitter", "X (Twitter)"],
  ["youtube", "YouTube"], ["linkedin", "LinkedIn"], ["whatsapp", "WhatsApp"], ["telegram", "Telegram"],
];

export default function SiteSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/site-settings").then((r) => setS({ social: {}, ...r.data })).catch(() => toast.error("Could not load settings"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/site-settings", {
        company_name: s.company_name, address: s.address, phone: s.phone, email: s.email,
        working_hours: s.working_hours, support_hours: s.support_hours, contact_intro: s.contact_intro,
        social: s.social || {},
      });
      setS({ social: {}, ...data });
      toast.success("Contact & social settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const setSocial = (k, v) => setS({ ...s, social: { ...(s.social || {}), [k]: v } });
  const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm outline-none focus:border-emerald-500";

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-site-settings">
      <div className="ledger-card rounded-2xl p-6">
        <h2 className="font-heading text-xl font-bold">Contact information</h2>
        <p className="mt-1 text-xs text-slate-500">Shown on the public Contact Us page and footer.</p>
        <div className="mt-6 space-y-4">
          <L label="Company name"><input data-testid="ss-company" value={s.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className={inputCls} /></L>
          <L label="Address"><textarea data-testid="ss-address" rows={3} value={s.address || ""} onChange={(e) => set("address", e.target.value)} className={inputCls} /></L>
          <div className="grid gap-4 sm:grid-cols-2">
            <L label="Phone"><input data-testid="ss-phone" value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></L>
            <L label="Email"><input data-testid="ss-email" value={s.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} /></L>
            <L label="Working hours"><input data-testid="ss-working" value={s.working_hours || ""} onChange={(e) => set("working_hours", e.target.value)} className={inputCls} /></L>
            <L label="Support hours"><input data-testid="ss-support" value={s.support_hours || ""} onChange={(e) => set("support_hours", e.target.value)} className={inputCls} /></L>
          </div>
          <L label="Contact page intro"><textarea data-testid="ss-intro" rows={3} value={s.contact_intro || ""} onChange={(e) => set("contact_intro", e.target.value)} className={inputCls} /></L>
        </div>
      </div>

      <div className="ledger-card rounded-2xl p-6">
        <div className="flex items-center gap-2"><Share2 className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">Social media links</h2></div>
        <p className="mt-1 text-xs text-slate-500">Paste full URLs. Leave blank to hide a platform.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {SOCIALS.map(([k, label]) => (
            <L key={k} label={label}>
              <input data-testid={`ss-social-${k}`} placeholder={`https://…`} value={(s.social || {})[k] || ""} onChange={(e) => setSocial(k, e.target.value)} className={inputCls} />
            </L>
          ))}
        </div>
      </div>

      <button data-testid="ss-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
      </button>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-widest text-slate-500">{label}</label>
      {children}
    </div>
  );
}
